import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import "../css/InstAdminClassForm.css";
import {
  FaCalendarAlt,
  FaChartArea,
  FaCheck,
  FaChevronDown,
  FaEye,
  FaFileAlt,
  FaPen,
  FaPlus,
  FaPowerOff,
  FaSchool,
  FaSearch,
  FaTrash,
} from "react-icons/fa";
import { MdDashboard, MdClass, MdPeople } from "react-icons/md";
import { HiSparkles } from "react-icons/hi2";
import TermSemesterForm from "./TermSemesterForm";

import { Button } from "@mui/material";
import {
  Box,
  Paper,
  Stack,
  Grid,
  Typography,
  IconButton,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  Skeleton,
  InputAdornment,
} from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import ConfirmModal from "../components/modal/confirm";
import AddTeacherModal from "../components/modal/teacherAdd";
import EditTeacherModal from "../components/modal/teacherEdit";
import TeachersGrid from "../components/Teachers";
import { motion, AnimatePresence } from "framer-motion";
import NewSeasonModal from "../components/InstEndSeason";

/* ─── Design Tokens ─────────────────────────────────────────── */
const T = {
  blue: "#1565C0",
  blueLight: "#1E88E5",
  blueMid: "#42A5F5",
  blueGhost: "rgba(21,101,192,0.07)",
  blueGhostHover: "rgba(21,101,192,0.13)",
  indigo: "#3949AB",
  teal: "#00796B",
  amber: "#F57C00",
  rose: "#C62828",
  roseGhost: "rgba(198,40,40,0.08)",
  slate900: "#0D1B2A",
  slate700: "#334155",
  slate500: "#64748B",
  slate400: "#94A3B8",
  slate200: "#E2E8F0",
  slate100: "#F1F5F9",
  slate50: "#F8FAFC",
  white: "#FFFFFF",
  grad: "linear-gradient(135deg, #1565C0 0%, #1E88E5 100%)",
  gradSoft:
    "linear-gradient(135deg, rgba(21,101,192,0.08) 0%, rgba(30,136,229,0.04) 100%)",
  shadow: "0 1px 3px rgba(13,27,42,0.08), 0 4px 16px rgba(13,27,42,0.04)",
  shadowMd: "0 4px 20px rgba(13,27,42,0.1), 0 1px 4px rgba(13,27,42,0.06)",
};

/* ─── Reusable Motion Wrappers ───────────────────────────────── */
const FadeIn = ({ children, delay = 0, y = 12, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

/* ─── Stat Pill ──────────────────────────────────────────────── */
const StatPill = ({ icon: Icon, value, label, color = T.blue }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.2,
      px: 2,
      py: 1,
      borderRadius: 99,
      background: `${color}12`,
      border: `1px solid ${color}28`,
    }}
  >
    <Icon size={13} color={color} />
    <Box>
      <Typography
        sx={{
          fontSize: "0.82rem",
          fontWeight: 800,
          color: T.slate900,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.62rem",
          color: T.slate400,
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

/* ─── Section Heading ────────────────────────────────────────── */
const SectionIcon = ({ gradient, icon: Icon }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: 2,
      flexShrink: 0,
      background: gradient,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    }}
  >
    <Icon size={15} color="#fff" />
  </Box>
);

/* ─── Class Card ─────────────────────────────────────────────── */
const ClassCard = ({
  cls,
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  deleteLoading,
  allTerms,
  doneTerms,
  navigateToReport,
}) => {
  const isApproved = cls.is_approved;
  const terms = isApproved ? allTerms : doneTerms;
  const sorted = [...(terms || [])].sort((a, b) =>
    (a?.term || "").localeCompare(b?.term || "")
  );

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          overflow: "hidden",
          border: isOpen
            ? `1.5px solid ${T.blue}50`
            : `1px solid ${T.slate200}`,
          background: T.white,
          transition: "all 0.25s ease",
          boxShadow: isOpen ? `0 6px 24px rgba(21,101,192,0.12)` : T.shadow,
          "&:hover": { boxShadow: T.shadowMd, borderColor: `${T.blue}35` },
        }}
      >
        {/* Row */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 2.5, py: 1.8, gap: 1.5 }}
        >
          {/* Status dot */}
          <Tooltip title={isApproved ? "Approved" : "Pending Approval"} arrow>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                flexShrink: 0,
                cursor: "help",
                background: isApproved ? T.teal : T.slate400,
                boxShadow: isApproved ? `0 0 0 3px ${T.teal}25` : "none",
              }}
            />
          </Tooltip>

          {/* Name & badge — clickable */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
            onClick={() => onToggle(cls.id)}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.9rem",
                color: T.slate900,
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {cls.name}
            </Typography>
            <Chip
              label={isApproved ? "Approved" : "Pending"}
              size="small"
              sx={{
                height: 19,
                fontSize: "0.6rem",
                fontWeight: 700,
                flexShrink: 0,
                background: isApproved ? `${T.teal}15` : T.slate100,
                color: isApproved ? T.teal : T.slate500,
                border: `1px solid ${isApproved ? T.teal + "35" : T.slate200}`,
              }}
            />
          </Stack>

          {/* Actions */}
          <Stack direction="row" spacing={0.7} flexShrink={0}>
            <Tooltip title="Edit class" arrow>
              <IconButton
                size="small"
                onClick={() => onEdit(cls)}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  color: T.blue,
                  background: T.blueGhost,
                  border: `1px solid ${T.blue}22`,
                  "&:hover": { background: T.blueGhostHover },
                }}
              >
                <FaPen size={11} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete class" arrow>
              <IconButton
                size="small"
                onClick={() => onDelete(cls.id)}
                disabled={deleteLoading === cls.id}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  color: T.rose,
                  background: T.roseGhost,
                  border: `1px solid ${T.rose}22`,
                  "&:hover": { background: `${T.rose}14` },
                  "&:disabled": { opacity: 0.4 },
                }}
              >
                {deleteLoading === cls.id ? (
                  <CircularProgress size={11} sx={{ color: T.rose }} />
                ) : (
                  <FaTrash size={11} />
                )}
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => onToggle(cls.id)}
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.5,
                color: T.blue,
                background: isOpen ? T.blueGhostHover : T.blueGhost,
                border: `1px solid ${T.blue}22`,
                transition: "transform 0.25s ease",
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                "&:hover": { background: T.blueGhostHover },
              }}
            >
              <FaChevronDown size={11} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Dropdown */}
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="dropdown"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: "hidden" }}
            >
              <Box
                sx={{ px: 2.5, pb: 2.5, borderTop: `1px solid ${T.slate100}` }}
              >
                <Typography
                  sx={{
                    pt: 2,
                    pb: 1.2,
                    fontSize: "0.62rem",
                    fontWeight: 800,
                    color: T.slate400,
                    textTransform: "uppercase",
                    letterSpacing: "0.9px",
                  }}
                >
                  {isApproved ? "All Terms" : "Completed Terms"}
                </Typography>
                {sorted.length > 0 ? (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {sorted.map((term) => (
                      <Chip
                        key={term.id}
                        label={term.term}
                        size="small"
                        onClick={() => navigateToReport(cls.id, term.id)}
                        icon={
                          <FaFileAlt
                            size={9}
                            style={{ marginLeft: 8, color: T.blue }}
                          />
                        }
                        sx={{
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          borderRadius: 1.5,
                          background: T.blueGhost,
                          color: T.blue,
                          border: `1px solid ${T.blue}28`,
                          "&:hover": {
                            background: T.blueGhostHover,
                            borderColor: T.blue,
                          },
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: T.slate400,
                      fontStyle: "italic",
                    }}
                  >
                    {isApproved ? "No terms found." : "No completed terms yet."}
                  </Typography>
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Paper>
    </motion.div>
  );
};

/* ─── Main Component ─────────────────────────────────────────── */
const InstHomePage = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [doneTerms, setDoneTerms] = useState([]);
  const [allTerms, setAllTerms] = useState([]);
  const [editingClass, setEditingClass] = useState(null);
  const [name, setName] = useState("");
  const [loadingCreateUpdate, setLoadingCreateUpdate] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [openClassId, setOpenClassId] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [filter, setFilter] = useState("");
  const [showWipeModal, setShowWipeModal] = useState(false);
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const [highlight, setHighlight] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, clsId: null });
  const [teachers, setTeachers] = useState([]);
  const [currentTeacher, setCurrentTeacher] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState("");
  const handleClose = () => setOpen(false);
  const [openAdd, setOpenAdd] = useState(false);
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const token = useMemo(() => {
    const tokenString = localStorage.getItem("authToken");
    return tokenString ? JSON.parse(tokenString) : null;
  }, []);

  const authAxios = useMemo(() => {
    const tokenString = localStorage.getItem("authToken");
    const token = tokenString ? JSON.parse(tokenString) : null;
    if (!token?.access) return null;
    return axios.create({
      baseURL: API_URL,
      headers: { Authorization: `Bearer ${token.access}` },
    });
  }, []);

  useEffect(() => {
    if (!authAxios) navigate("/login");
  }, [authAxios, navigate]);

  useEffect(() => {
    if (!authAxios) return;
    const id = authAxios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401) navigate("/login");
        return Promise.reject(err);
      }
    );
    return () => authAxios.interceptors.response.eject(id);
  }, [authAxios, navigate]);

  useEffect(() => {
    if (!authAxios) return;
    setLoading(true);
    authAxios
      .get("/inst-teachers/")
      .then((res) => {
        const updated = res.data.map((t) => ({
          ...t,
          tempName: t.name,
          tempIsClassAdmin: t.is_class_admin,
          tempSelectedClasses: t.instclass_ids || [],
        }));
        updated.sort((a, b) => a.name.localeCompare(b.name));
        setTeachers(updated);
      })
      .catch((err) => console.error("Error fetching teachers:", err))
      .finally(() => setLoading(false));
  }, [authAxios, refresh]);

  const fetchData = async () => {
    if (!token) {
      navigate("/login");
      return;
    }
    setLoadingList(true);
    try {
      const res = await fetch(`${API_URL}/instclass/`, {
        headers: { Authorization: `Bearer ${token.access}` },
      });
      if (res.ok) {
        const data = await res.json();
        setClasses(Array.isArray(data) ? data : data.classes || []);
        setDoneTerms(data.done_terms || []);
        setAllTerms(data.all_terms || []);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, token]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_URL}/student/profile/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        });
        const data = res.data;
        if (data.is_inst && (!data.name || !data.profile_pic)) {
          toast.warning(
            "Please update your institution name and upload your school logo before accessing the dashboard."
          );
          navigate("/instprofile");
        }
      } catch (error) {
        toast.error("Unable to load profile");
      }
    };
    if (token?.access) fetchProfile();
  }, [token, navigate]);

  const sortedFilteredClasses = [...classes]
    .filter((c) => c?.name?.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) =>
      (a?.name || "").localeCompare(b?.name || "", undefined, {
        sensitivity: "base",
      })
    );

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setName(cls.name || "");
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlight(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };

  const clearForm = () => {
    setEditingClass(null);
    setName("");
    setHighlight(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (!token) {
      navigate("/login");
      return;
    }
    setLoadingCreateUpdate(true);
    try {
      const method = editingClass ? "PUT" : "POST";
      const endpoint = editingClass
        ? `${API_URL}/instclass/${editingClass.id}/`
        : `${API_URL}/instclass/`;
      const res = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.access}`,
        },
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        const updated = editingClass
          ? classes.map((cls) => (cls.id === editingClass.id ? data : cls))
          : [...classes, data];
        setClasses(updated);
        clearForm();
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Connection error. Please try again later.");
    } finally {
      setLoadingCreateUpdate(false);
    }
  };

  const handleDelete = (clsId) => {
    if (!token) {
      navigate("/login");
      return;
    }
    setDeleteModal({ open: true, clsId });
  };

  const confirmDelete = async () => {
    const { clsId } = deleteModal;
    setDeleteLoadingId(clsId);
    try {
      const res = await fetch(`${API_URL}/instclass/${clsId}/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.access}`,
        },
      });
      if (res.ok) {
        setClasses((prev) => prev.filter((cls) => cls.id !== clsId));
        if (editingClass?.id === clsId) clearForm();
        setDeleteModal({ open: false, clsId: null });
      } else {
        const err = await res.json();
        toast.error("Delete failed: " + JSON.stringify(err));
        setDeleteModal({ open: false, clsId: null });
      }
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const toggleClassDropdown = (classId) => {
    setOpenClassId((prev) => (prev === classId ? null : classId));
  };
  const navigateToReport = (instclassId, doneTermId) => {
    navigate(
      `/instaminstudreport?instclassId=${instclassId}&done_termId=${doneTermId}`
    );
  };

  const filteredTeachers = teachers.filter(
    (t) =>
      t.name.toLowerCase().includes(teacherSearch.toLowerCase()) ||
      t.code.toLowerCase().includes(teacherSearch.toLowerCase())
  );

  const startEditing = (teacher) => {
    const prepared = {
      ...teacher,
      tempName: teacher.name || "",
      tempIsClassAdmin: !!teacher.is_class_admin,
      tempEmail: teacher.email || "",
      tempPhone: teacher.phone || "",
      tempGender: teacher.gender || "",
      tempPassport: teacher.passport || null,
      tempNewPassport: null,
      tempAddress: teacher.address || "",
      tempQualification: teacher.qualification || "",
      tempYears_of_experience: teacher.years_of_experience || 0,
      tempDate_of_birth: teacher.date_of_birth || "",
    };
    setCurrentTeacher(prepared);
    setOpen(true);
  };

  const handleCreate = async ({ name, isClassAdmin, email, phone, gender }) => {
    if (!authAxios) return;
    try {
      const res = await authAxios.post("/inst-teachers/", {
        name,
        is_class_admin: isClassAdmin,
        email,
        phone,
        gender,
      });
      toast.success(`Teacher Code: ${res.data.code}`);
      setRefresh((p) => !p);
      handleCloseAdd();
    } catch (err) {
      console.error("Create error", err);
    }
  };

  const handleUpdate = async () => {
    if (!authAxios || !currentTeacher) return;
    try {
      const formData = new FormData();
      formData.append("name", currentTeacher.tempName);
      formData.append("email", currentTeacher.tempEmail);
      formData.append("phone", currentTeacher.tempPhone);
      formData.append("gender", currentTeacher.tempGender);
      formData.append("is_class_admin", currentTeacher.tempIsClassAdmin);
      formData.append("address", currentTeacher.tempAddress);
      formData.append(
        "years_of_experience",
        currentTeacher.tempYears_of_experience
      );
      formData.append("date_of_birth", currentTeacher.tempDate_of_birth);
      formData.append("qualification", currentTeacher.tempQualification);
      if (currentTeacher.tempNewPassport)
        formData.append("passport", currentTeacher.tempNewPassport);
      const res = await authAxios.put(
        `/inst-teachers/${currentTeacher.id}/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const updatedTeacher = res.data;
      setTeachers((prev) =>
        prev
          .map((t) =>
            t.id === updatedTeacher.id
              ? {
                  ...t,
                  ...updatedTeacher,
                  passport: updatedTeacher.passport || t.passport,
                }
              : t
          )
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      toast.success("Updated Successfully");
      setOpen(false);
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const downloadFile = async (url, filename) => {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token.access}`,
        Accept: filename.endsWith(".pdf") ? "application/pdf" : "text/csv, */*",
      },
    });
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ detail: "Unknown Error" }));
      throw new Error(err.detail || "Server failed");
    }
    const blob = await response.blob();
    if (blob.size === 0)
      throw new Error("Received an empty file from the server.");
    const mimeType = filename.endsWith(".pdf") ? "application/pdf" : blob.type;
    const downloadUrl = window.URL.createObjectURL(
      new Blob([blob], { type: mimeType })
    );
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    return true;
  };

  const handleDownloadTeacherCSV = async () => {
    try {
      await downloadFile(
        `${API_URL}/teachers/download/csv/`,
        "teachers_data.csv"
      );
    } catch (error) {
      toast.error("Failed to download CSV");
    }
  };

  const handleDownloadTeacherPDF = async () => {
    const loadingToast = toast.loading("Generating PDF… please wait.");
    try {
      await downloadFile(
        `${API_URL}/teachers/download/pdf/`,
        "teachers_data.pdf"
      );
      toast.update(loadingToast, {
        render: "PDF Downloaded! 📄",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      toast.update(loadingToast, {
        render: error.message || "Failed to download PDF",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  /* ─ RENDER ─────────────────────────────────────────────────── */
  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          background: `radial-gradient(ellipse 120% 50% at 60% -10%, rgba(21,101,192,0.07) 0%, transparent 60%), ${T.slate50}`,
          fontFamily: "'DM Sans', 'Nunito', system-ui, sans-serif",
        }}
      >
        {/* ── Top Nav Bar ─────────────────────────────────────── */}
        <FadeIn>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 100,
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(16px)",
              borderBottom: `1px solid ${T.slate200}`,
              px: { xs: 2, md: 4 },
              py: 1.5,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
            >
              {/* Brand & breadcrumb */}
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={0.8}>
                  <MdDashboard size={16} color={T.blue} />
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: T.slate400,
                      fontWeight: 600,
                    }}
                  >
                    Dashboard
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: T.slate300 }}>
                    /
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: T.slate700,
                      fontWeight: 700,
                    }}
                  >
                    Classes & Staff
                  </Typography>
                </Stack>
              </Stack>

              {/* Actions */}
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                alignItems="center"
              >
                <Button
                  onClick={() => navigate("/teachers-login")}
                  variant="outlined"
                  size="small"
                  startIcon={<FaEye size={11} />}
                  sx={{
                    borderRadius: 99,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.76rem",
                    px: 2,
                    py: 0.75,
                    borderColor: `${T.blue}35`,
                    color: T.blue,
                    "&:hover": { borderColor: T.blue, background: T.blueGhost },
                  }}
                >
                  View Students
                </Button>
                <Button
                  onClick={() => navigate("/analysis-dashboard")}
                  variant="outlined"
                  size="small"
                  startIcon={<FaChartArea size={11} />}
                  sx={{
                    borderRadius: 99,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.76rem",
                    px: 2,
                    py: 0.75,
                    borderColor: `${T.blue}35`,
                    color: T.blue,
                    "&:hover": { borderColor: T.blue, background: T.blueGhost },
                  }}
                >
                  Analytics
                </Button>
                <Button
                  onClick={() => setShowWipeModal(true)}
                  variant="contained"
                  size="small"
                  startIcon={<FaPowerOff size={11} />}
                  sx={{
                    borderRadius: 99,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.76rem",
                    px: 2,
                    py: 0.75,
                    background: "linear-gradient(135deg, #C62828, #E53935)",
                    boxShadow: "0 3px 10px rgba(198,40,40,0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #B71C1C, #C62828)",
                    },
                  }}
                >
                  End Session
                </Button>
              </Stack>
            </Stack>
          </Box>
        </FadeIn>

        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: { xs: 2.5, md: 4 },
            maxWidth: 1400,
            mx: "auto",
          }}
        >
          {/* ── Page Title ────────────────────────────────────── */}
          <FadeIn delay={0.05}>
            <Box sx={{ mb: 4 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                gap={2}
              >
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    mb={0.5}
                  >
                    <HiSparkles size={18} color={T.amber} />
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        color: T.amber,
                        textTransform: "uppercase",
                        letterSpacing: "1.2px",
                      }}
                    >
                      Academic Management
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      color: T.slate900,
                      letterSpacing: "-0.8px",
                      lineHeight: 1.1,
                    }}
                  >
                    Classes & Staff
                  </Typography>
                  <Typography
                    sx={{ color: T.slate500, mt: 0.5, fontSize: "0.88rem" }}
                  >
                    Manage your classes, academic terms, and teaching staff in
                    one place.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  <StatPill
                    icon={MdClass}
                    value={sortedFilteredClasses.length}
                    label="Classes"
                    color={T.blue}
                  />
                  <StatPill
                    icon={MdPeople}
                    value={filteredTeachers.length}
                    label="Teachers"
                    color={T.teal}
                  />
                  <StatPill
                    icon={FaCalendarAlt}
                    value={allTerms.length}
                    label="Terms"
                    color={T.indigo}
                  />
                </Stack>
              </Stack>
            </Box>
          </FadeIn>

          {showWipeModal && (
            <NewSeasonModal
              token={token}
              onClose={() => setShowWipeModal(false)}
              open={() => setShowWipeModal(true)}
            />
          )}

          {/* ══ SECTION 1: Classes & Terms ══════════════════════════ */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* LEFT: Forms */}
            <Grid item xs={12} md={4}>
              <FadeIn delay={0.1}>
                <Stack spacing={3}>
                  {/* Terms Form Card */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      border: `1px solid ${T.slate200}`,
                      boxShadow: T.shadow,
                    }}
                  >
                    <Box
                      sx={{
                        px: 3,
                        py: 2.2,
                        borderBottom: `1px solid ${T.slate100}`,
                        background:
                          "linear-gradient(135deg, rgba(57,73,171,0.05) 0%, rgba(63,81,181,0.02) 100%)",
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <SectionIcon
                          gradient="linear-gradient(135deg, #3949AB, #5C6BC0)"
                          icon={FaCalendarAlt}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: T.slate900,
                              fontSize: "0.92rem",
                            }}
                          >
                            Terms & Semesters
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.71rem", color: T.slate400 }}
                          >
                            Configure academic periods
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <TermSemesterForm
                        token={token}
                        allTerms={allTerms}
                        refreshTerms={fetchData}
                      />
                    </Box>
                  </Paper>

                  {/* Create / Edit Class Form Card */}
                  <Paper
                    ref={formRef}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      border: highlight
                        ? `1.5px solid ${T.blue}`
                        : `1px solid ${T.slate200}`,
                      boxShadow: highlight
                        ? `0 0 0 4px ${T.blue}15, ${T.shadowMd}`
                        : T.shadow,
                      transition: "all 0.4s ease",
                    }}
                  >
                    <Box
                      sx={{
                        px: 3,
                        py: 2.2,
                        borderBottom: `1px solid ${T.slate100}`,
                        background: T.gradSoft,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <SectionIcon
                          gradient={T.grad}
                          icon={editingClass ? FaPen : FaPlus}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: T.slate900,
                              fontSize: "0.92rem",
                            }}
                          >
                            {editingClass ? "Edit Class" : "Create Class"}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.71rem", color: T.slate400 }}
                          >
                            {editingClass
                              ? "Update class details"
                              : "Add a new class or department"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <Box component="form" onSubmit={handleSubmit}>
                        <Stack spacing={1.5}>
                          <TextField
                            inputRef={inputRef}
                            placeholder="e.g. JSS 1A, Grade 10, Science Dept."
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            size="small"
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                background: T.slate50,
                                color: T.slate900,
                                "& fieldset": { borderColor: T.slate200 },
                                "&:hover fieldset": { borderColor: T.blue },
                                "&.Mui-focused fieldset": {
                                  borderColor: T.blue,
                                  borderWidth: 1.5,
                                },
                              },
                              "& input::placeholder": {
                                color: T.slate400,
                                fontSize: "0.84rem",
                              },
                            }}
                          />
                          <Stack direction="row" spacing={1}>
                            <Button
                              type="submit"
                              variant="contained"
                              disabled={loadingCreateUpdate}
                              fullWidth
                              startIcon={
                                loadingCreateUpdate ? null : editingClass ? (
                                  <FaCheck size={11} />
                                ) : (
                                  <FaPlus size={11} />
                                )
                              }
                              sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 700,
                                background: T.grad,
                                boxShadow: "0 4px 14px rgba(21,101,192,0.3)",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #0D47A1, #1565C0)",
                                },
                                "&:disabled": { opacity: 0.5 },
                              }}
                            >
                              {loadingCreateUpdate ? (
                                <CircularProgress
                                  size={16}
                                  sx={{ color: "#fff" }}
                                />
                              ) : editingClass ? (
                                "Update Class"
                              ) : (
                                "Create Class"
                              )}
                            </Button>
                            {editingClass && (
                              <Button
                                type="button"
                                variant="outlined"
                                onClick={clearForm}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: "none",
                                  fontWeight: 700,
                                  borderColor: T.slate200,
                                  color: T.slate500,
                                  "&:hover": {
                                    borderColor: T.slate400,
                                    background: T.slate50,
                                  },
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </Box>
                    </Box>
                  </Paper>
                </Stack>
              </FadeIn>
            </Grid>

            {/* RIGHT: Class List */}
            <Grid item xs={12} md={8}>
              <FadeIn delay={0.15}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `1px solid ${T.slate200}`,
                    boxShadow: T.shadow,
                    height: "100%",
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 3,
                      py: 2.2,
                      borderBottom: `1px solid ${T.slate100}`,
                      background: T.gradSoft,
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      gap={1.5}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <SectionIcon
                          gradient="linear-gradient(135deg, #00796B, #26A69A)"
                          icon={FaSchool}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: T.slate900,
                              fontSize: "0.92rem",
                            }}
                          >
                            All Classes
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.71rem", color: T.slate400 }}
                          >
                            {sortedFilteredClasses.length} class
                            {sortedFilteredClasses.length !== 1
                              ? "es"
                              : ""}{" "}
                            found
                          </Typography>
                        </Box>
                      </Stack>
                      <TextField
                        placeholder="Search classes…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FaSearch size={12} color={T.slate400} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          minWidth: { xs: "100%", sm: 220 },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            background: T.white,
                            color: T.slate900,
                            fontSize: "0.84rem",
                            "& fieldset": { borderColor: T.slate200 },
                            "&:hover fieldset": { borderColor: T.blue },
                            "&.Mui-focused fieldset": { borderColor: T.blue },
                          },
                          "& input::placeholder": { color: T.slate400 },
                        }}
                      />
                    </Stack>
                  </Box>

                  {/* Body */}
                  <Box sx={{ p: 2.5 }}>
                    {loadingList ? (
                      <Grid container spacing={1.5}>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <Grid item xs={12} key={i}>
                            <Skeleton
                              variant="rounded"
                              height={58}
                              sx={{ borderRadius: 2.5, bgcolor: `${T.blue}08` }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    ) : sortedFilteredClasses.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 8 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 3,
                            background: T.blueGhost,
                            border: `1px dashed ${T.blue}35`,
                            mx: "auto",
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaSchool size={24} color={T.slate400} />
                        </Box>
                        <Typography
                          sx={{
                            color: T.slate700,
                            fontWeight: 700,
                            fontSize: "0.9rem",
                          }}
                        >
                          No classes found
                        </Typography>
                        <Typography
                          sx={{
                            color: T.slate400,
                            fontSize: "0.78rem",
                            mt: 0.5,
                          }}
                        >
                          Try adjusting your search or create a new class.
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={1.2}>
                        {sortedFilteredClasses.map((cls) => (
                          <ClassCard
                            key={cls.id}
                            cls={cls}
                            isOpen={openClassId === cls.id}
                            onToggle={toggleClassDropdown}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            deleteLoading={deleteLoadingId}
                            allTerms={allTerms}
                            doneTerms={doneTerms}
                            navigateToReport={navigateToReport}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </FadeIn>
            </Grid>
          </Grid>

          {/* ══ SECTION 2: Teachers ════════════════════════════════ */}
          <FadeIn delay={0.2}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${T.slate200}`,
                boxShadow: T.shadow,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: 3,
                  py: 2.2,
                  borderBottom: `1px solid ${T.slate100}`,
                  background: T.gradSoft,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <SectionIcon
                      gradient="linear-gradient(135deg, #F57C00, #FFA726)"
                      icon={MdPeople}
                    />
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: T.slate900,
                          fontSize: "0.92rem",
                        }}
                      >
                        Teaching Staff
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.71rem", color: T.slate400 }}
                      >
                        {filteredTeachers.length} teacher
                        {filteredTeachers.length !== 1 ? "s" : ""} found
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <TextField
                      placeholder="Search by name or code…"
                      value={teacherSearch}
                      onChange={(e) => setTeacherSearch(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaSearch size={12} color={T.slate400} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        minWidth: { xs: "100%", sm: 220 },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          background: T.white,
                          color: T.slate900,
                          fontSize: "0.84rem",
                          "& fieldset": { borderColor: T.slate200 },
                          "&:hover fieldset": { borderColor: T.blue },
                          "&.Mui-focused fieldset": { borderColor: T.blue },
                        },
                        "& input::placeholder": { color: T.slate400 },
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<FaPlus size={10} />}
                      onClick={handleOpenAdd}
                      sx={{
                        borderRadius: 99,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        px: 2.5,
                        py: 0.9,
                        background: T.grad,
                        whiteSpace: "nowrap",
                        boxShadow: "0 4px 14px rgba(21,101,192,0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #0D47A1, #1565C0)",
                        },
                      }}
                    >
                      Add Teacher
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <TeachersGrid
                  loading={loading}
                  teachers={filteredTeachers}
                  onEdit={startEditing}
                  handleDownloadTeacherCSV={handleDownloadTeacherCSV}
                  handleDownloadTeacherPDF={handleDownloadTeacherPDF}
                />
              </Box>
            </Paper>
          </FadeIn>
        </Box>
      </Box>

      {/* ── Modals ─────────────────────────────────────────────── */}
      <AddTeacherModal
        open={openAdd}
        onClose={handleCloseAdd}
        onCreate={handleCreate}
      />
      <EditTeacherModal
        open={open}
        onClose={handleClose}
        teacher={currentTeacher}
        setTeacher={setCurrentTeacher}
        onSave={handleUpdate}
      />
      <ConfirmModal
        isOpen={deleteModal.open}
        title="Delete Class"
        message="Are you sure you want to delete this class? All associated data will be permanently removed."
        confirmLabel="Delete"
        loading={deleteLoadingId === deleteModal.clsId}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal({ open: false, clsId: null })}
      />
    </>
  );
};

export default InstHomePage;
